import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { itineraryAPI } from '../services/api';
import './ItineraryHistory.css';

const INTERESTS = ['Culture Hunter', 'Thrill Seeker', 'Natural Wonder', 'Foodie Trail', 'Retail Adventure'];
const PLAN_TYPES = ['ai_generated', 'preset'];

const ItineraryHistory = () => {
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  // Filters
  const [search, setSearch] = useState('');
  const [planType, setPlanType] = useState('');
  const [interest, setInterest] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isArchived, setIsArchived] = useState('');

  const fetchItineraries = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        limit: itemsPerPage,
        page: currentPage,
        ...(search && { search }),
        ...(planType && { plan_type: planType }),
        ...(interest && { interest }),
        ...(fromDate && { from_date: fromDate }),
        ...(toDate && { to_date: toDate }),
        ...(isArchived !== '' && { is_archived: isArchived }),
      };
      const data = await itineraryAPI.getAll(params);
      setItineraries(data.data || data.itineraries || []);
      setTotalItems(data.pagination?.total || data.total || 0);
    } catch {
      setError('Failed to load itineraries.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, planType, interest, fromDate, toDate, isArchived]);

  useEffect(() => {
    const t = setTimeout(fetchItineraries, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchItineraries, search]);

  const handleArchive = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Archive this itinerary?')) return;
    try {
      await itineraryAPI.archive(id);
      setSuccess('Itinerary archived.');
      setTimeout(() => setSuccess(''), 3000);
      fetchItineraries();
    } catch {
      setError('Failed to archive itinerary.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearFilters = () => {
    setSearch(''); setPlanType(''); setInterest('');
    setFromDate(''); setToDate(''); setIsArchived('');
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <Layout title="Itinerary History">
      <div className="itin-history">
        <div className="itin-page-header">
          <h2>Itinerary History</h2>
          <span className="itin-total-badge">{totalItems} total</span>
        </div>

        {error   && <div className="itin-alert error">{error}</div>}
        {success && <div className="itin-alert success">{success}</div>}

        {/* Filters */}
        <div className="itin-filters">
          <input
            type="text"
            placeholder="Search user, interests..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            className="itin-input"
          />
          <select value={planType} onChange={e => { setPlanType(e.target.value); setCurrentPage(1); }} className="itin-select">
            <option value="">All Types</option>
            {PLAN_TYPES.map(t => <option key={t} value={t}>{t === 'ai_generated' ? 'AI Generated' : 'Preset'}</option>)}
          </select>
          <select value={interest} onChange={e => { setInterest(e.target.value); setCurrentPage(1); }} className="itin-select">
            <option value="">All Interests</option>
            {INTERESTS.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setCurrentPage(1); }} className="itin-input date" />
          <input type="date" value={toDate}   onChange={e => { setToDate(e.target.value);   setCurrentPage(1); }} className="itin-input date" />
          <select value={isArchived} onChange={e => { setIsArchived(e.target.value); setCurrentPage(1); }} className="itin-select">
            <option value="">All Status</option>
            <option value="false">Active</option>
            <option value="true">Archived</option>
          </select>
          <button className="itin-clear-btn" onClick={clearFilters}>Clear</button>
        </div>

        {/* Table */}
        <div className="itin-table-wrap">
          <table className="itin-table">
            <thead>
              <tr>
                <th></th>
                <th>ID</th>
                <th>User</th>
                <th>Type</th>
                <th>Interests</th>
                <th>Dates</th>
                <th>Guests</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="itin-td-center">Loading...</td></tr>
              ) : itineraries.length === 0 ? (
                <tr><td colSpan={10} className="itin-td-center">No itineraries found.</td></tr>
              ) : itineraries.map(item => (
                <React.Fragment key={item.id}>
                  <tr
                    className={`itin-row ${expandedRows.has(item.id) ? 'expanded' : ''}`}
                    onClick={() => toggleRow(item.id)}
                  >
                    <td className="itin-expand-toggle">{expandedRows.has(item.id) ? '▼' : '▶'}</td>
                    <td className="itin-id">{String(item.id).slice(0, 8)}…</td>
                    <td>{item.user_id || '—'}</td>
                    <td>
                      <span className={`itin-type-badge ${item.plan_type === 'ai_generated' ? 'ai' : 'preset'}`}>
                        {item.plan_type === 'ai_generated' ? '🤖 AI' : '🗂️ Preset'}
                      </span>
                    </td>
                    <td>
                      <div className="itin-interests">
                        {(() => {
                          // Prefer backend-computed display_interests; fallback to parsing locally
                          let chips = [];
                          if (Array.isArray(item.display_interests) && item.display_interests.length > 0) {
                            chips = item.display_interests;
                          } else {
                            try {
                              const parsed = typeof item.interests === 'string'
                                ? JSON.parse(item.interests)
                                : (Array.isArray(item.interests) ? item.interests : []);
                              chips = parsed.some(i => i.length > 30) ? [item.title || 'Custom'] : parsed;
                            } catch { chips = item.title ? [item.title] : []; }
                          }
                          return chips.filter(Boolean).map(i => (
                            <span key={i} className="itin-chip">{i}</span>
                          ));
                        })()}
                      </div>
                    </td>
                    <td>{item.from_date ? `${item.from_date} → ${item.to_date}` : '—'}</td>
                    <td>{item.guests ?? '—'}</td>
                    <td>
                      {item.is_archived
                        ? <span className="itin-status archived">Archived</span>
                        : <span className="itin-status active">Active</span>}
                    </td>
                    <td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <div className="itin-action-btns">
                        <button
                          className="itin-btn-icon view"
                          title="View detail"
                          onClick={e => { e.stopPropagation(); navigate(`/itinerary/history/${item.id}`); }}
                        >👁️</button>
                        {!item.is_archived && (
                          <button
                            className="itin-btn-icon archive"
                            title="Archive"
                            onClick={e => handleArchive(item.id, e)}
                          >📦</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedRows.has(item.id) && (
                    <tr className="itin-expanded-row">
                      <td colSpan={10}>
                        <div className="itin-expanded-content">
                          <div className="itin-expanded-card">
                            <div className="itin-expanded-title">{item.title || '—'}</div>
                            <div className="itin-expanded-meta">
                              {item.days_count ? `${item.days_count} days` : '—'}
                              {item.activities_count ? ` · ${item.activities_count} activities` : ''}
                            </div>
                            <button
                              className="itin-btn-view-full"
                              onClick={e => { e.stopPropagation(); navigate(`/itinerary/history/${item.id}`); }}
                            >
                              View Full Details →
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="itin-pagination">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>◀ Prev</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ▶</button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ItineraryHistory;
